import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { promptLabService } from '../../services/PromptLabService';

export function PromptLabList() {
  const navigate = useNavigate();
  
  const [usageTypes, setUsageTypes] = useState<any[]>([]);
  const [loadingUsageTypes, setLoadingUsageTypes] = useState(true);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  
  // Pagination state for prompts
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Pagination state for usage types
  const [usageTypePage, setUsageTypePage] = useState(0);
  const [usageTypeRowsPerPage, setUsageTypeRowsPerPage] = useState(10);
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  const initialLoading = loadingUsageTypes || loadingPrompts;

  useEffect(() => {
    const fetchUsageTypes = async () => {
      try {
        const data = await promptLabService.getUsageTypes();
        setUsageTypes(data);
      } catch (error) {
        console.error('Failed to load usage types:', error);
      } finally {
        setLoadingUsageTypes(false);
      }
    };

    fetchUsageTypes();
  }, []);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const data = await promptLabService.getPrompts();
        setPrompts(data);
      } catch (error) {
        console.error('Failed to load prompts:', error);
      } finally {
        setLoadingPrompts(false);
      }
    };

    fetchPrompts();
  }, []);

  const handleAddNew = () => {
    navigate(ROUTE_PATHS.PROMPT_LAB_ADD);
  };

  const handleEdit = (prompt: any) => {
    navigate(ROUTE_PATHS.PROMPT_LAB_EDIT(prompt.id));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUsageTypeChangePage = (event: unknown, newPage: number) => {
    setUsageTypePage(newPage);
  };

  const handleUsageTypeChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsageTypeRowsPerPage(parseInt(event.target.value, 10));
    setUsageTypePage(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Get paginated data
  const paginatedPrompts = prompts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const paginatedUsageTypes = usageTypes.slice(
    usageTypePage * usageTypeRowsPerPage,
    usageTypePage * usageTypeRowsPerPage + usageTypeRowsPerPage
  );

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {initialLoading ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '60vh',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ color: '#64748b' }}>
              Loading Template...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#0f172a',
                    mb: 1
                  }}
                >
                  Prompt Template
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#64748b'
                  }}
                >
                  Manage your prompt templates
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNew}
                >
                  Add Prompt Template
                </Button>
              </Box>
            </Box>
              <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <MuiTable>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Usage Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Prompt Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Version Count</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Updated By</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingPrompts ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={40} sx={{ color: '#667eea' }} />
                      </TableCell>
                    </TableRow>
                  ) : prompts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          No prompts found. Click "Add New Prompt" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedPrompts.map((row: any) => {
                      const usageType = usageTypes.find(type => type.id === row.usage_type);
                      
                      return (
                        <TableRow 
                          key={row.id} 
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: '#f8fafc',
                              cursor: 'pointer'
                            },
                            '&:last-child td': {
                              borderBottom: 0
                            }
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>{row.name || 'N/A'}</TableCell>
                          <TableCell sx={{ py: 2 }}>{usageType?.name || 'N/A'}</TableCell>
                          <TableCell sx={{ py: 2 }}>{row.prompt_type === 'system_prompt' ? 'System Prompt' : 'User Prompt'}</TableCell>
                          <TableCell sx={{ py: 2 }}>{row.versions.length || 'N/A'}</TableCell>
                          <TableCell sx={{ py: 2 }}>{row.updated_by}</TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              sx={{ 
                                color: '#667eea',
                                '&:hover': {
                                  bgcolor: '#eef2ff'
                                }
                              }}
                              title="Edit prompt"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </MuiTable>
              
              {/* Pagination */}
              {prompts.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={prompts.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid #e2e8f0',
                    bgcolor: '#f8fafc',
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      color: '#64748b'
                    }
                  }}
                />
              )}
            </TableContainer>
          </>
        )}
      </Container>
    </Box>
  );
}
